import {
	Att,
	AttrPath,
	DoorstroomResultaat,
	FieldSchema,
	FkSchema,
	getFkSchema,
	isArrayFkSchema,
	isFkSchema,
	Niveau,
	tableSchemaMap,
} from '@cumlaude/metadata';
import { difference, get, intersection, set } from 'lodash-es';

export type AttMap = {
	[key in Att]?: string;
};

const attMap: AttMap = {};

function buildWithForeignKey(fkSchema: FkSchema, column: string) {
	for (const [foreignKeyColumn, foreignKeyVal] of Object.entries(fkSchema.schema.properties)) {
		if (isFkSchema(foreignKeyVal) || isArrayFkSchema(foreignKeyVal) || !('label' in foreignKeyVal)) continue;
		const fieldSchema = foreignKeyVal as FieldSchema;

		const labelOverride = fkSchema.labelOverride?.[foreignKeyColumn];
		let label: string | undefined;
		if (labelOverride) {
			label = labelOverride;
		} else {
			label = fieldSchema.label;
			const labelSuffix = fkSchema.labelSuffix;
			if (labelSuffix) label = `${label} ${labelSuffix}`;
		}

		set(attMap, `${column}.${foreignKeyColumn}`, label);
	}
}

function build() {
	for (const schema of Object.values(tableSchemaMap)) {
		for (const [column, val] of Object.entries(schema.properties)) {
			const fkSchema = getFkSchema(val);
			if (fkSchema) {
				buildWithForeignKey(fkSchema, column);
			} else {
				set(attMap, column, (val as FieldSchema).label);
			}
		}
	}
}

build();

export const nonBreakingSpace = '\u00a0';

export function niveau_afk(niveau: Niveau): string {
	const map: { [n in Niveau]: string } = {
		'te breed': 'Onb',
		OND: 'lj',
		VSO: 'vso',
		PrO: 'p',
		'VMBO b': 'b',
		'VMBO b / k': 'bk',
		'VMBO k': 'k',
		'VMBO k / (g)t': 'kt',
		'VMBO (g)t': 't',
		'VMBO (g)t / HAVO': 'th',
		'VMBO (g)t / HAVO / VWO': 'lj',
		VMBO: 'lj',
		'VMBO / HAVO': 'lj',
		'VMBO / HAVO / VWO': 'lj',
		HAVO: 'h',
		'HAVO / VWO': 'hv',
		VWO: 'v',
		//        'International': 'int'
	};
	return map[niveau] ?? 'Onb';
}

export function idu_afk(idu: DoorstroomResultaat | null, extraKort: boolean): string {
	const normaalMap: { [n in DoorstroomResultaat]: string } = {
		Afgewezen: 'Afgewezen',
		Afstroom: 'Af',
		Doorstroom: 'Door',
		Doublure: 'Dou',
		Geen: 'Geen',
		Geslaagd: 'Geslaagd',
		Opstroom: 'Op',
		'VO verlater': 'VO-verl',
	};
	const extraKortMap: { [n in DoorstroomResultaat]: string } = {
		Afgewezen: 'Afg',
		Afstroom: 'Af',
		Doorstroom: 'Door',
		Doublure: 'Dou',
		Geen: 'Geen',
		Geslaagd: 'Gesl',
		Opstroom: 'Op',
		'VO verlater': 'VO-v',
	};
	const map = extraKort ? extraKortMap : normaalMap;
	return idu ? map[idu] : 'Onb';
}

export function attrLabel(attrPath: AttrPath): string {
	const label = get(attMap, attrPath);
	if (label === undefined) {
		console.warn(`Geen label gedefinieerd voor [${attrPath.join('.')}]. Voeg deze toe aan de correcte spec file.`);
	}

	return label;
}

export type DoorstroomDoel = {
	'ds_fk_lb_van.lb_nm_niveau_volgend_sj': Niveau | null;
	'ds_fk_lb_van.lb_nr_leerjaar_volgend_sj': string | null;
	ds_nm_idu: DoorstroomResultaat | null;
	ds_fun_nm_brinvest_overgang?: string | null;
};

export function doorstroom_afk(attrs: DoorstroomDoel, extraKort = false): string {
	const { 'ds_fk_lb_van.lb_nr_leerjaar_volgend_sj': nr_leerjaar_naar, ds_nm_idu, 'ds_fk_lb_van.lb_nm_niveau_volgend_sj': nm_niveau_naar } = attrs;
	if (nm_niveau_naar && nr_leerjaar_naar) return niveau_afk(nm_niveau_naar) + nr_leerjaar_naar;
	else if (nm_niveau_naar) return niveau_afk(nm_niveau_naar) + '? ' + idu_afk(ds_nm_idu, extraKort);
	else if (nr_leerjaar_naar) return '?' + nr_leerjaar_naar + ' ' + idu_afk(ds_nm_idu, extraKort);
	else return idu_afk(ds_nm_idu, extraKort);
}

export function roepAchternaam(medewerker: { mw_nm_roepnaam: string; mw_nm_voorvoegsel: string; mw_nm_achternaam: string }) {
	return [medewerker.mw_nm_roepnaam, medewerker.mw_nm_voorvoegsel, medewerker.mw_nm_achternaam].filter(Boolean).join(' ');
}

export function sortLike<T>(input: T[], order: T[]): T[] {
	const known = intersection(order, input);
	const unknown = difference(input, order);
	return [...known, ...unknown];
}
